import type { DialogRoot } from "@base-ui/react/dialog";
import { ModalLayout } from "@/components/modal-layout/modal-layout";
import { Dialog, DialogPopup } from "@/components/ui/dialog";
import type {
    ChoiceModalDescriptor,
    InfoModalDescriptor,
    SimpleModalDescriptor,
} from "@/services/simple-modal/simple-modal-types";
import { useStore } from "@/store/store";
import { ChoiceSimpleModal } from "./components/choice-simple-modal";
import { InfoSimpleModal } from "./components/info-simple-modal";
import { rejectChoice, resolveChoice, resolveInfo } from "./resolver-registry";

const finishChoice = (modal: ChoiceModalDescriptor, value: string): void => {
    resolveChoice(modal.requestId, value);
    useStore.getState().closeSimpleModal();
};

const finishChoiceError = (modal: ChoiceModalDescriptor, reason: unknown): void => {
    rejectChoice(modal.requestId, reason);
    useStore.getState().closeSimpleModal();
};

const finishInfo = (modal: InfoModalDescriptor): void => {
    resolveInfo(modal.requestId);
    useStore.getState().closeSimpleModal();
};

// Ensures that we resolve the modal state regardless of how the modal is closed
const handleOpenChange = (
    modal: SimpleModalDescriptor,
    nextOpen: boolean,
    details: DialogRoot.ChangeEventDetails,
): void => {
    if (nextOpen) {
        return;
    }
    if (!modal.dismissible) {
        details.cancel();
        return;
    }
    if (modal.type === "choice") {
        // Backdrop click and Esc resolve the dismiss value; they never run a button's onSelect.
        finishChoice(modal, modal.dismissValue);
        return;
    }
    finishInfo(modal);
};

// The X is wired separately from `dismissible`, so it still works on a modal that ignores backdrop clicks.
const closeControl = (descriptor: SimpleModalDescriptor): (() => void) | undefined => {
    if (descriptor.type === "choice") {
        return descriptor.showClose ? () => finishChoice(descriptor, descriptor.dismissValue) : undefined;
    }
    return descriptor.dismissible ? () => finishInfo(descriptor) : undefined;
};

export function SimpleModal() {
    const descriptor = useStore((s) => s.simpleModalDescriptor);
    const isOpen = useStore((s) => s.isSimpleModalOpen);

    return (
        <Dialog
            disablePointerDismissal={descriptor ? !descriptor.dismissible : true}
            onOpenChange={(open, eventDetails) => descriptor && handleOpenChange(descriptor, open, eventDetails)}
            onOpenChangeComplete={(open) => {
                // Guard against rapid open→close→open: a new modal may have opened before this
                // fires, so only clear content if the dialog is still genuinely closed.
                if (!open && !useStore.getState().isSimpleModalOpen) {
                    useStore.getState().clearSimpleModalDescriptor();
                }
            }}
            open={isOpen}
        >
            {/*
             * Confirms and choices are a line of text and a row of buttons, so they take the dialog's default
             * width; at md they spread a sentence across 768px. Info modals keep md for the rich content
             * they carry (the CSV import help, the weather card's about panel).
             */}
            <DialogPopup showCloseButton={false} size={descriptor?.type === "info" ? "md" : "default"}>
                {descriptor && (
                    <ModalLayout
                        key={descriptor.requestId}
                        title={descriptor.title ?? undefined}
                        onClickClose={closeControl(descriptor)}
                    >
                        {descriptor.type === "choice" ? (
                            <ChoiceSimpleModal
                                modal={descriptor}
                                onError={(reason) => finishChoiceError(descriptor, reason)}
                                onSelect={(value) => finishChoice(descriptor, value)}
                            />
                        ) : (
                            <InfoSimpleModal modal={descriptor} onAcknowledge={() => finishInfo(descriptor)} />
                        )}
                    </ModalLayout>
                )}
            </DialogPopup>
        </Dialog>
    );
}
